import { Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export type Language = 'en' | 'ar';

export interface LanguageOption {
  code: Language;
  name: string;
  nativeName: string;
  dir: 'ltr' | 'rtl';
  flag: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  // Available languages
  readonly languages: LanguageOption[] = [
    {
      code: 'en',
      name: 'English',
      nativeName: 'English',
      dir: 'ltr',
      flag: '🇬🇧'
    },
    {
      code: 'ar',
      name: 'Arabic',
      nativeName: 'العربية',
      dir: 'rtl',
      flag: '🇸🇦'
    }
  ];

  // Current language signal
  currentLanguage = signal<Language>('en');
  isRTL = signal<boolean>(false);

  constructor(private translate: TranslateService) {
    // Initialize TranslateService with available languages and fallback
    this.translate.addLangs(['en', 'ar']);
    this.translate.setDefaultLang('en');

    this.initializeLanguage();
  }

  /**
   * Initialize language from storage or browser
   */
  private initializeLanguage(): void {
    // Try to get from localStorage
    const savedLang = localStorage.getItem('language') as Language;

    if (savedLang && this.isValidLanguage(savedLang)) {
      this.setLanguage(savedLang);
    } else {
      // Auto-detect from browser
      const browserLang = this.translate.getBrowserLang();
      const detectedLang = browserLang === 'ar' ? 'ar' : 'en';
      this.setLanguage(detectedLang);
    }
  }

  /**
   * Set the current language
   */
  setLanguage(lang: Language): void {
    if (!this.isValidLanguage(lang)) {
      console.warn(`Invalid language: ${lang}. Defaulting to 'en'`);
      lang = 'en';
    }

    // Update translation service
    this.translate.use(lang);

    // Update signals
    this.currentLanguage.set(lang);
    this.isRTL.set(lang === 'ar');

    // Save to localStorage
    localStorage.setItem('language', lang);

    // Update HTML attributes
    this.updateDocumentAttributes(lang);
  }

  /**
   * Get current language
   */
  getCurrentLanguage(): Language {
    return this.currentLanguage();
  }

  /**
   * Get current language option
   */
  getCurrentLanguageOption(): LanguageOption {
    return this.languages.find(l => l.code === this.currentLanguage()) || this.languages[0];
  }

  /**
   * Check if current language is RTL
   */
  isRTLLanguage(): boolean {
    return this.isRTL();
  }

  /**
   * Toggle between languages
   */
  toggleLanguage(): void {
    const newLang: Language = this.currentLanguage() === 'en' ? 'ar' : 'en';
    this.setLanguage(newLang);
  }

  /**
   * Update document HTML attributes for RTL support
   */
  private updateDocumentAttributes(lang: Language): void {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;

    // Set lang attribute
    htmlElement.setAttribute('lang', lang);

    // Set dir attribute
    const dir = lang === 'ar' ? 'rtl' : 'ltr';
    htmlElement.setAttribute('dir', dir);
    bodyElement.setAttribute('dir', dir);

    // Add/remove RTL class
    if (lang === 'ar') {
      bodyElement.classList.add('rtl');
      bodyElement.classList.remove('ltr');
    } else {
      bodyElement.classList.add('ltr');
      bodyElement.classList.remove('rtl');
    }
  }

  /**
   * Validate language code
   */
  private isValidLanguage(lang: string): lang is Language {
    return lang === 'en' || lang === 'ar';
  }

  /**
   * Get translation for a key
   */
  instant(key: string, params?: any): string {
    return this.translate.instant(key, params);
  }

  /**
   * Get translation observable for a key
   */
  get(key: string, params?: any) {
    return this.translate.get(key, params);
  }
}
